import { decodeClampedImage } from '@/components/Recipe/resourceLoading';
import { CompositorAiError, CompositorAiErrorCode } from './compositorAiError';
import type { OutpaintSourceCrop } from 'web';

export interface OutpaintSource {
  url: string;
  width: number;
  height: number;
}

export function getOutpaintSourceDimensions(width: number, height: number, crop?: OutpaintSourceCrop) {
  return crop
    ? { width: Math.max(1, Math.round(width * crop.width)), height: Math.max(1, Math.round(height * crop.height)) }
    : { width, height };
}

export async function cropOutpaintSource(
  source: OutpaintSource,
  crop?: OutpaintSourceCrop,
  signal?: AbortSignal,
): Promise<OutpaintSource> {
  signal?.throwIfAborted();
  if (!crop) return source;
  const { bitmap } = await decodeClampedImage(source.url, Number.POSITIVE_INFINITY);
  const canvas = document.createElement('canvas');
  try {
    signal?.throwIfAborted();
    const { width, height } = getOutpaintSourceDimensions(source.width, source.height, crop);
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) throw new CompositorAiError(CompositorAiErrorCode.SourceUnavailable);
    context.drawImage(
      bitmap,
      crop.x * bitmap.width,
      crop.y * bitmap.height,
      crop.width * bitmap.width,
      crop.height * bitmap.height,
      0,
      0,
      width,
      height,
    );
    const url = canvas.toDataURL('image/png');
    if (!url.startsWith('data:image/png')) throw new CompositorAiError(CompositorAiErrorCode.SourceUnavailable);
    signal?.throwIfAborted();
    return { url, width, height };
  } finally {
    bitmap.close();
    canvas.width = canvas.height = 0;
  }
}
